const isRaise = (bb, a) => a.kind === 'raise' || (a.kind === 'jam' && (a.amount ?? 0) > bb);
const isJam = (a) => a.kind === 'jam';
/**
 * Derive hero's preflop facing line + position. `role` selection:
 *   • no preflop raise                → 'open'   (first-in; limpers counted)
 *   • one raise, hero hasn't raised   → 'defend' (opener = the raiser)
 *   • hero opened, faces a re-raise   → 'vs3bet' (2 raises) / 'vs4bet' (≥3 raises)
 *   • a jam faces hero                → 'vsjam'
 * A non-opener facing a 3-bet (a squeeze target) maps to 'defend' vs the first raiser.
 */
export const deriveHeroPreflopSpot = (state, heroSeat) => {
    const hero = state.players.find((p) => p.seat === heroSeat);
    if (!hero)
        throw new Error(`deriveHeroPreflopSpot: hero seat ${heroSeat} not in hand`);
    const bb = state.spec.stakes.bb;
    const preflop = state.history.filter((h) => h.street === 'preflop').map((h) => h.action);
    const raises = preflop.filter((a) => isRaise(bb, a));
    const nRaises = raises.length;
    const heroRaises = raises.filter((a) => a.seat === heroSeat).length;
    const firstRaiser = raises[0];
    const openerPosition = firstRaiser !== undefined ? state.players.find((p) => p.seat === firstRaiser.seat)?.position : undefined;
    // effective stack = min(hero, largest live opponent) in bb.
    const liveOpp = state.players.filter((p) => p.seat !== heroSeat && !p.folded).map((p) => p.stack);
    const eff = liveOpp.length > 0 ? Math.min(hero.stack, Math.max(...liveOpp)) : hero.stack;
    const effectiveStackBB = eff / bb;
    const toCallBB = Math.max(0, (state.currentBet - hero.committedThisStreet) / bb);
    // does the action facing hero end in an all-in jam?
    const lastNonHero = [...preflop].reverse().find((a) => a.seat !== heroSeat);
    const facingJam = lastNonHero !== undefined && isJam(lastNonHero) && (lastNonHero.amount ?? 0) > bb;
    const base = { effectiveStackBB, toCallBB };
    let facing;
    if (facingJam) {
        facing = { role: 'vsjam', ...base, ...(openerPosition ? { openerPosition } : {}) };
    }
    else if (nRaises === 0) {
        const limpers = preflop.filter((a) => a.kind === 'call').length;
        facing = { role: 'open', ...base, limpers };
    }
    else if (heroRaises > 0 && raises[0]?.seat === heroSeat && nRaises >= 2) {
        // hero opened and now faces a re-raise.
        facing = {
            role: nRaises >= 3 ? 'vs4bet' : 'vs3bet',
            ...base,
            ...(openerPosition ? { openerPosition } : {}),
        };
    }
    else {
        // hero has not raised (or isn't the original opener): defend vs the (first) raiser. Callers between
        // the opener and hero drive the squeeze/iso-sizing signal.
        const firstIdx = preflop.findIndex((a) => isRaise(bb, a));
        const callers = preflop.slice(firstIdx + 1).filter((a) => a.kind === 'call' && a.seat !== heroSeat).length;
        facing = {
            role: 'defend',
            ...base,
            ...(openerPosition ? { openerPosition } : {}),
            callers,
        };
    }
    return { heroPosition: hero.position, facing };
};
//# sourceMappingURL=heroSpot.js.map